import type { ApiSite } from '@/services/marketplaceService';
import type { Site, FilterState } from './types';

/**
 * Transform API site response to frontend Site shape.
 */
export function transformApiSite(api: ApiSite): Site {
  const num = (v: unknown): number => Number(v) || 0;

  const guestPostPrice = num(api.pricing?.guest_post);
  const linkInsertionPrice = num(api.pricing?.link_insertion);
  const visualLinkPrice = num(api.pricing?.visual_link);

  return {
    id: api.id,
    domain: api.domain,
    domain_type: api.domain_type,
    title: api.name,
    description: api.description ?? '',
    category:
      typeof api.category === 'object' && api.category !== null
        ? api.category.name
        : (api.category ?? ''),
    niche:
      typeof api.niche === 'object' && api.niche !== null
        ? api.niche.name
        : (api.niche ?? ''),
    tags: Array.isArray(api.tags) ? api.tags : [],
    // SEO
    da: num(api.seo_metrics?.domain_authority),
    pa: num(api.seo_metrics?.page_authority),
    dr: num(api.seo_metrics?.domain_rating),
    tf: num(api.seo_metrics?.trust_flow),
    cf: num(api.seo_metrics?.citation_flow),
    spam_score: num(api.seo_metrics?.spam_score),
    organic_traffic: num(api.seo_metrics?.organic_traffic),
    // Services
    services: {
      guest_posting: {
        enabled: api.service_info?.offers_guest_posts ?? guestPostPrice > 0,
        price: guestPostPrice,
      },
      link_insertion: {
        enabled:
          api.service_info?.offers_link_insertion ?? linkInsertionPrice > 0,
        price: linkInsertionPrice,
      },
      visual_link: {
        enabled: api.service_info?.offers_visual_link ?? visualLinkPrice > 0,
        price: visualLinkPrice,
      },
    },
    // Location
    languages: api.language ? [api.language.name] : [],
    countries:
      Array.isArray(api.leading_countries) && api.leading_countries.length > 0
        ? api.leading_countries
        : api.country
          ? [api.country.name]
          : [],
    country_name: api.country?.name ?? '',
    language_name: api.language?.name ?? api.primary_language ?? '',
    // Link
    link_type: api.link_type?.name ?? '',
    link_validity: api.link_validity?.name ?? '',
    max_link_count: api.maximum_link_count ?? 0,
    mark_sponsored: api.mark_sponsored ?? false,
    // Service info
    turnaround_days: api.service_info?.turnaround_time_days ?? 0,
    // Grey niche
    grey_niche:
      Array.isArray(api.grey_niche_prices) && api.grey_niche_prices.length > 0,
    grey_niche_prices: (api.grey_niche_prices ?? []).map((gn) => ({
      id: gn.id,
      grey_niche_id: gn.grey_niche_id,
      name: gn.grey_niche?.name ?? '',
      price: num(gn.selling_price),
    })),
    // Status
    verified: api.verification_status === 'verified',
    featured: false,
    last_updated: api.last_verified_at ?? api.created_at ?? '',
  };
}

/**
 * Quick action handler — updates filter state.
 */
export const handleQuickAction = (
  action: string,
  setFilters: (updater: (prev: FilterState) => FilterState) => void
) => {
  switch (action) {
    case 'filter_da_high':
      setFilters((prev) => ({ ...prev, da_range: [90, 100] }));
      break;
    case 'filter_recent':
      setFilters((prev) => ({
        ...prev,
        sort_by: 'created_at',
        sort_order: 'desc' as const,
      }));
      break;
    case 'filter_trending':
      setFilters((prev) => ({
        ...prev,
        sort_by: 'organic_traffic',
        sort_order: 'desc' as const,
      }));
      break;
  }
};

/**
 * Default filter state.
 */
export const getInitialFilters = (): FilterState => ({
  search: '',
  category: 'all',
  niche: 'all',
  language: 'all',
  da_range: [0, 100],
  dr_range: [0, 100],
  spam_score_max: 100,
  organic_traffic_min: 0,
  guest_post_price_min: 0,
  guest_post_price_max: 0,
  link_insertion_price_min: 0,
  link_insertion_price_max: 0,
  visual_link_price_min: 0,
  visual_link_price_max: 0,
  offers_guest_posts: false,
  offers_link_insertion: false,
  offers_visual_link: false,
  max_turnaround_days: 0,
  sort_by: 'domain_authority',
  sort_order: 'desc',
});
